import React from 'react';
import { newCareersDict } from '@common/const/catalog';
import { Grid } from '@cp/ds/src/components/grid';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { CareerId } from '@common/const/catalog/career';
import { useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { useRouter } from '@cp/shared/router';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';
import { ClientType } from '@common/model/client/clientType';
import { CareerTile, coreCareerApi } from '@cp/entities/career';
import { useIsMobile } from '@cp/ds/src/hooks';
import { Result } from '@cp/shared/result';
import { RoutedLink as Link } from 'src/shared/ui/link';
import { useClientSelectionService, usePrefilter, useQuizFeature } from 'src/features/clientSelection';
import { useAuth } from 'src/features/auth';
import { CatalogCareerListProps } from './types';

export const ITEM_SPACING_MOBILE = 7;
export const ITEM_SPACING_DESKTOP = 20;

export const CatalogCareerList: React.FC<CatalogCareerListProps> = ({ sx, itemType = CatalogItemType.Resume, enableLinks }) => {
  const isMobile = useIsMobile();
  const router = useRouter();
  const routeResolver = useClientWebResolver();
  const { data: careerDictionary } = coreCareerApi.endpoints.getCareers.useQuery();
  const items = Object.entries({ ...careerDictionary, ...(!isMobile && newCareersDict) }).map(
    ([careerId, careerViewModel]) => [Number(careerId), careerViewModel] as const,
  );
  const quiz = useQuizFeature();
  const prefilter = usePrefilter();
  const auth = useAuth();
  const clientSelectionService = useClientSelectionService();

  return (
    <Grid
      container
      spacing={{ xs: `${ITEM_SPACING_MOBILE}px`, md: `${ITEM_SPACING_DESKTOP}px` }}
      sx={mergeSx(
        {
          position: 'relative',
          zIndex: 1,
          pb: 4.5,
          width: '100%',
        },
        sx,
      )}
    >
      {items.map(([careerId, careerViewModel]) => {
        if (!careerViewModel) {
          return null;
        }

        const url =
          enableLinks && CareerId[careerId]
            ? String(
                routeResolver.resolve({
                  page: ClientWebPage.Catalog,
                  pageProps: { catalogType: itemType, careerId },
                }),
              )
            : undefined;
        return (
          <Grid item key={careerId} lg={2} md={4} xs={6}>
            <Link href={url} sx={{ textDecoration: 'none' }}>
              <CareerTile
                careerId={careerId}
                label={careerViewModel.translation.namePlural}
                onClick={
                  'isActive' in careerViewModel && !careerViewModel.isActive
                    ? undefined
                    : async () => {
                        clientSelectionService.register({ careerId });
                        const firstQuiz = await quiz(['clientType']);
                        await firstQuiz.flatMapAsync<unknown, unknown>(({ clientType }) => {
                          switch (clientType) {
                            case ClientType.Specialist:
                              return Result.fromAsync(() =>
                                router.navigate(
                                  routeResolver.resolve({
                                    page: ClientWebPage.Catalog,
                                    pageProps: { catalogType: clientTypeToSuitableCatalogItemType(clientType), careerId },
                                  }),
                                ),
                              );
                            case ClientType.Employer:
                              return prefilter({ requiredQuestions: ['cityId'], omitQuestions: ['careerId'] }).then((r) =>
                                auth(r.unwrap()),
                              );
                            default:
                              throw new Error('Unknown client type');
                          }
                        });
                      }
                }
              />
            </Link>
          </Grid>
        );
      })}
    </Grid>
  );
};
